import React from 'react';
import NavigationItem from 'src/pagesDashboard/HomePage/components/mobile/NavigationItem';
import {
  Button, Base, toast, Select
} from 'src/components/';
import { useForm } from 'react-hook-form';
import FlagSVG from 'src/assets/svg/flag.svg';
import STYLE from 'src/constants/style';
import appUtils from 'src/components/appUtils';
import commonGoalUtils from 'common/commonGoalUtils';
import COMMON_CONSTANTS from 'common/commonConstants';
import { useGoalUpdate, QUERY_KEYS as GOAL_QUERY_KEYS } from 'src/queries/goal';
import { get, isEmpty } from 'lodash';
import commonDateUtils from 'common/commonDateUtils';
import { getGoalStatuses } from 'src/containers/UserProfile/Goals/constants';
import { queryClient } from 'src/index';
import commonViewPermissions from 'common/commonViewPermissions';
import { useTree } from 'src/queries/tree';

const { GOALS } = COMMON_CONSTANTS;

const MobileAddGoalUpdate = ({ goal, setShowAddUpdate }) => {
  const {
    data: { tree },
    isFetching: isFetchingTree,
    isError: isErrorTree
  } = useTree();

  const statusOptions = getGoalStatuses();

  const { watch, reset, setValue } = useForm({
    defaultValues: {
      text: '',
      progress: commonGoalUtils.getGoalProgressOption(goal),
      status: commonGoalUtils.getGoalStatusOptionFromLatestUpdate(
        goal,
        statusOptions
      )
    }
  });

  const formValues = watch();
  const { text, progress, status } = formValues;
  const resetForm = () => {
    const defaultValues = {
      text: '',
      progress: commonGoalUtils.getGoalProgressOption(goal),
      status: commonGoalUtils.getGoalStatusOptionFromLatestUpdate(
        goal,
        statusOptions
      )
    };
    reset(defaultValues);
  };

  const { mutateAsync: updateGoal, isLoading: isLoadingGoalUpdate } = useGoalUpdate(goal.id);

  const isFetching = isFetchingTree;
  const isError = isErrorTree;
  const isReady = !isFetching && !isError;

  if (!isReady) {
    return null;
  }

  const isBinary = goal.type === GOALS.TYPES.BINARY;
  const isPercent = goal.type === GOALS.TYPES.PERCENT;

  const loggedUserId = appUtils.getLoggedUserId();
  const canManageGoal = commonViewPermissions.canManageGoal(
    tree,
    loggedUserId,
    goal.assignee
  );

  const addUpdate = async () => {
    try {
      const payload = {
        text,
        author: loggedUserId,
        timestamp: commonDateUtils.getUnixDateNow(),
        status: status.value,
        progress: get(progress, 'value', progress)
      };

      const response = await updateGoal({ updates: [payload] });

      if (!response || !response.success) {
        return toast.error('Try again!');
      }

      if (response.warning) {
        const message = get(
          response,
          'message',
          'Failed to add update. Try again later!'
        );
        toast.closeAll();
        return toast.show(message);
      }

      toast.show('Update added!');

      setShowAddUpdate(false);

      await queryClient.refetchQueries([GOAL_QUERY_KEYS.GOALS, goal.id]);
      await queryClient.invalidateQueries(GOAL_QUERY_KEYS.GOALS_LOOKUP);

      resetForm();
    } catch (error) {
      return toast.error(error ?? 'Try again!');
    }
  };

  const renderProgressField = () => {
    if (isBinary) {
      const options = [
        { value: 0, label: 'Not done' },
        { value: 1, label: 'Done' }
      ];

      return (
        <Select
          classes='w-full'
          optionsWidth='fit'
          maxOptionWidth='500px'
          placeholder='Any progress?'
          options={options}
          title={get(progress, 'label')}
          disabled={isLoadingGoalUpdate}
          onChange={(option) => setValue('progress', option)}
        />
      );
    }

    return (
      <input
        className='p-2 border border-mid-gray bg-white rounded-lg resize-none w-full'
        placeholder='Any progress?'
        type='number'
        min={isPercent ? 0 : undefined}
        max={isPercent ? 100 : undefined}
        value={progress}
        disabled={isLoadingGoalUpdate}
        onChange={(e) => setValue('progress', e.target.value)}
      />
    );
  };

  const getIsDataValid = () => {
    if (isEmpty(text) || text.length < 10) {
      return false;
    }

    if (isEmpty(status.value)) {
      return false;
    }

    if (isBinary) {
      if (!isFinite(progress.value)) {
        return false;
      }
    } else if (!isFinite(progress)) {
      return false;
    }

    if (isPercent && (progress < 0 || progress > 100)) {
      return false;
    }

    return true;
  };

  return (
    <Base classes={STYLE.MOBILE_CONTAINER_NO_SCROLL_MARGINLESS}>
      <div>
        <div className='w-full fixed top-0 mt-12 bg-white z-50'>
          <NavigationItem
            goBack={() => setShowAddUpdate(false)}
            title={goal.title}
          >
            <FlagSVG />
          </NavigationItem>
        </div>
      </div>
      <div className='px-3 mt-[87px]'>
        <div className='flex'>
          <div className='w-full'>
            <p className='mb-1'>Status</p>
            <Select
              classes='w-full'
              optionsWidth='fit'
              maxOptionWidth='500px'
              options={statusOptions}
              title={get(status, 'label')}
              disabled={isLoadingGoalUpdate}
              onChange={(option) => setValue('status', option)}
            />
          </div>
        </div>
        <div className='w-full'>
          <p className='mb-1 mt-3'>Progress</p>
          {renderProgressField()}
        </div>
        <div className='w-full mt-3 mb-24'>
          <p className='mb-1'>Update</p>
          <textarea
            className='w-full p-2 border border-mid-gray bg-white rounded-lg resize-none h-24'
            value={text}
            placeholder='Add an update'
            disabled={!canManageGoal || isLoadingGoalUpdate}
            onChange={(e) => setValue('text', e.target.value)}
          />
        </div>
      </div>
      <div className='w-full flex flex-col items-center px-6 pb-2 gap-3 mt-1 fixed bottom-0 bg-white'>
        <Button
          variant='black'
          classes='w-full'
          disabled={isLoadingGoalUpdate || !getIsDataValid()}
          onClick={addUpdate}
        >
          Add Update
        </Button>
        <Button
          variant='custom'
          disabled={isLoadingGoalUpdate}
          classes='bg-white w-full text-black font-bold text-sm px-4 pt-2 pb-2 border rounded-md border-gray-300'
          onClick={() => setShowAddUpdate(false)}
        >
          Cancel
        </Button>
      </div>
    </Base>
  );
};

export default MobileAddGoalUpdate;
